/**
 * PagoRapidoDialog (F84) — modal global para que cajeros apliquen pagos
 * en 3 clicks sin navegar entre pantallas.
 *
 * Flujo:
 *   1. Tipea nombre/código del cliente → debounce 300ms → resultados
 *   2. Click en cliente → expande sus facturas pendientes/vencidas
 *   3. Click "Resolver" en una factura → AplicarPagoDialog (toggle pago/promesa)
 *   4. Submit → POST /api/v1/pagos o /api/v1/cobranza/promesas
 *
 * F95: el mini-form interno fue reemplazado por el AplicarPagoDialog
 * compartido para tener un único lugar donde se cobra/promete.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Search, ChevronDown, ChevronRight, Wallet, Loader2, CheckCircle2,
} from "lucide-react";

import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useDebounce } from "@/hooks/useDebounce";
import { apiCall } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { formatMXN } from "@/lib/utils";
import { AplicarPagoDialog } from "@/features/facturas/AplicarPagoDialog";
import { AplicarSaldoFavorDialog } from "@/features/pagos/AplicarSaldoFavorDialog";
import { useFactura } from "@/features/facturas/api";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

type ClienteSummary = {
  id: string;
  codigo: string;
  razon_social: string;
  nombre_comercial: string | null;
  estatus: string;
};

type PagedClientes = {
  items: ClienteSummary[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

type FacturaSummary = {
  id: string;
  folio: string;
  cliente_id: string;
  contrato_id: string | null;
  fecha_emision: string;
  fecha_vencimiento: string;
  monto_total: string;
  saldo: string;
  estatus: string;
};

type PagedFacturas = {
  items: FacturaSummary[];
  total: number;
};

export function PagoRapidoDialog({ open, onOpenChange }: Props) {
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [expandedClienteId, setExpandedClienteId] = useState<string | null>(null);
  // F95 — usamos el AplicarPagoDialog compartido (con toggle pago/promesa).
  // Solo guardamos el ID de la factura y dejamos que AplicarPagoDialog haga
  // su propio fetch via useFactura.
  const [payingFacturaId, setPayingFacturaId] = useState<string | null>(null);
  const { data: payingFactura } = useFactura(payingFacturaId);
  // rc24 — cliente que va a recibir un saldo a favor (sin factura específica).
  const [saldoFavorCliente, setSaldoFavorCliente] = useState<{
    id: string; nombre: string;
  } | null>(null);
  // rc95 — Los cajeros NO pueden registrar saldo a favor (pago sin factura):
  // se equivocaban y dejaban créditos huérfanos. Solo se esconde el botón
  // para rol cajero; el backend también lo bloquea (defense in depth).
  const user = useAuthStore((s) => s.user);
  const esCajero = (user?.rol?.nombre ?? "") === "cajero";

  // Reset al cerrar
  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setExpandedClienteId(null);
      setPayingFacturaId(null);
      setSaldoFavorCliente(null);
    }
  }, [open]);

  // Búsqueda fuzzy de clientes
  const { data: clientes, isLoading: searching } = useQuery({
    queryKey: ["pago-rapido", "clientes", search],
    queryFn: () =>
      apiCall<PagedClientes>("get", "/api/v1/clientes", {
        searchParams: { search, page: 1, size: 8 },
      }),
    enabled: open && search.length >= 2,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet size={16} /> Pago rápido
          </DialogTitle>
          <DialogDescription>
            Busca al cliente por nombre, código o teléfono y aplica un pago sin salir de aquí.
          </DialogDescription>
        </DialogHeader>

        {/* Buscador */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setExpandedClienteId(null); }}
            placeholder="Empieza a escribir el nombre o código del cliente…"
            className="pl-9 text-sm"
            autoFocus
          />
        </div>

        {/* Resultados */}
        {search.length < 2 ? (
          <div className="rounded-md border bg-muted/20 p-4 text-center text-xs text-muted-foreground">
            Escribe al menos 2 caracteres para buscar.
          </div>
        ) : searching && !clientes ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 size={16} className="animate-spin text-muted-foreground" />
          </div>
        ) : !clientes || clientes.items.length === 0 ? (
          <div className="rounded-md border bg-muted/20 p-4 text-center text-xs text-muted-foreground">
            Sin resultados para "{search}".
          </div>
        ) : (
          <div className="rounded-md border bg-card divide-y">
            {clientes.items.map((c) => (
              <ClienteRow
                key={c.id}
                cliente={c}
                expanded={expandedClienteId === c.id}
                onToggle={() =>
                  setExpandedClienteId(expandedClienteId === c.id ? null : c.id)
                }
                onPagar={(f) => setPayingFacturaId(f.id)}
                onSaldoFavor={() =>
                  setSaldoFavorCliente({ id: c.id, nombre: c.razon_social })
                }
                permitirSaldoFavor={!esCajero}
              />
            ))}
          </div>
        )}
      </DialogContent>

      {/* F95 — AplicarPagoDialog compartido (con toggle pago/promesa) */}
      {payingFactura && (
        <AplicarPagoDialog
          open={Boolean(payingFacturaId)}
          onOpenChange={(v) => {
            if (!v) {
              setPayingFacturaId(null);
              // Cierre del sub-dialog: cerrar también el Pago Rápido para
              // que el cajero vea el toast y arranque uno nuevo desde cero.
              setExpandedClienteId(null);
              setSearchInput("");
            }
          }}
          factura={payingFactura}
        />
      )}

      {/* rc24 — Aplicar saldo a favor cuando el cliente está al corriente */}
      {saldoFavorCliente && (
        <AplicarSaldoFavorDialog
          open={Boolean(saldoFavorCliente)}
          onOpenChange={(v) => {
            if (!v) {
              setSaldoFavorCliente(null);
              setExpandedClienteId(null);
              setSearchInput("");
            }
          }}
          clienteId={saldoFavorCliente.id}
          clienteNombre={saldoFavorCliente.nombre}
        />
      )}
    </Dialog>
  );
}

// ─── Fila cliente expandible ────────────────────────────────────────────
function ClienteRow({
  cliente, expanded, onToggle, onPagar, onSaldoFavor, permitirSaldoFavor,
}: {
  cliente: ClienteSummary;
  expanded: boolean;
  onToggle: () => void;
  onPagar: (f: FacturaSummary) => void;
  onSaldoFavor: () => void;
  permitirSaldoFavor: boolean;
}) {
  const { data: facturas, isLoading } = useQuery({
    queryKey: ["pago-rapido", "facturas", cliente.id],
    queryFn: () =>
      apiCall<PagedFacturas>("get", "/api/v1/facturas", {
        // F92.1 — backend ahora acepta estatus separados por coma vía in_().
        // size:50 para no perder pendientes en clientes con muchas facturas.
        searchParams: { cliente_id: cliente.id, estatus: "pendiente,parcial,vencida", page: 1, size: 50 },
      }),
    enabled: expanded,
  });

  // Defensa extra — solo facturas con saldo > 0 y ordenadas por vencimiento
  // (las más viejas/vencidas primero, así el cajero ve qué cobrar primero).
  const pendientes = (facturas?.items ?? [])
    .filter((f) => Number(f.saldo) > 0)
    .sort((a, b) => a.fecha_vencimiento.localeCompare(b.fecha_vencimiento));

  return (
    <div>
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/30"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <div>
            <div className="text-sm font-medium">{cliente.razon_social}</div>
            <div className="text-[10px] text-muted-foreground font-mono">
              {cliente.codigo}
              {cliente.nombre_comercial && (
                <span className="ml-2">· {cliente.nombre_comercial}</span>
              )}
            </div>
          </div>
        </div>
        <Badge
          variant={cliente.estatus === "activo" ? "success" :
                  cliente.estatus === "suspendido" ? "destructive" : "secondary"}
          className="text-[9px]"
        >
          {cliente.estatus}
        </Badge>
      </button>

      {expanded && (
        <div className="border-t bg-muted/10 p-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 size={14} className="animate-spin text-muted-foreground" />
            </div>
          ) : pendientes.length === 0 ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 size={14} />
                Sin facturas pendientes — el cliente está al corriente
              </div>
              {/* rc95: los cajeros no ven el botón de saldo a favor — solo
                  pueden cobrar facturas pendientes (backend también bloquea) */}
              {permitirSaldoFavor ? (
                <>
                  <Button
                    size="sm" variant="outline" onClick={onSaldoFavor}
                    className="w-full"
                  >
                    <Wallet size={12} className="mr-1.5" />
                    Aplicar saldo a favor
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center">
                    Crédito anticipado — se aplicará automáticamente a la próxima factura.
                  </p>
                </>
              ) : (
                <p className="text-[10px] text-muted-foreground text-center">
                  Nada por cobrar. Los pagos anticipados (saldo a favor) los registra un administrador.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              {pendientes.map((f) => (
                <div
                  key={f.id}
                  className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono">{f.folio}</div>
                    <div className="text-[10px] text-muted-foreground">
                      Vence {f.fecha_vencimiento} · {f.estatus}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold font-mono">{formatMXN(Number(f.saldo))}</div>
                    <div className="text-[10px] text-muted-foreground">
                      de {formatMXN(Number(f.monto_total))}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => onPagar(f)}>
                    Resolver
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

